<script setup lang="ts">
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useEvaluationStore, useOrganizationStore } from '@/store';
import { can, usePersonTypes } from '@/composables';
import AutoComplete from 'primevue/autocomplete';
import ScrollPanel from 'primevue/scrollpanel';
import DisciplineIcon from '@/components/DisciplineIcon/DisciplineIcon.vue';
import { PERMISSIONS } from '@/enum';

interface EvaluationPropsInterface {
  organizationsIds?: number[];
}

const props = defineProps<EvaluationPropsInterface>();
const { organizationsIds } = toRefs(props);

const evaluationStore = useEvaluationStore();
const organizationStore = useOrganizationStore();

const { completed, overdueStudents, upcomingStudents, loading, selectedStudentId, students } =
  storeToRefs(evaluationStore);

const organizationsIdsSelected = computed(() => organizationStore.organizationsIdsSelected);

const emit = defineEmits(['scheduleEvent', 'reload']);

const selectedStudent = ref();

const filteredStudents = ref(students.value);

onMounted(() => init());

watch(selectedStudent, () => {
  if (selectedStudent.value?.id) {
    selectedStudentId.value = selectedStudent.value.id;
    return;
  }

  if (!selectedStudent.value?.id) {
    selectedStudentId.value = 0;
  }
});

const openScheduleEvent = async (studentId: number, dueAt: string) => {
  const [year, month, day] = dueAt.split('-').map((part) => Number(part));
  const dueDate = new Date(year, month - 1, day);
  emit('scheduleEvent', studentId, dueDate);
};

const init = async () => {
  const organizationIdToSee = !can([PERMISSIONS.WIDGET_EVALUATION_ALL]) ? organizationsIds?.value : [];
  const organizationsIdToEvaluate: number[] | undefined =
    organizationsIdsSelected.value.length == 0 ? organizationIdToSee : organizationsIdsSelected.value;

  await evaluationStore.getEvaluations(organizationsIdToEvaluate || []);
};

const searchStudent = (event) => {
  if (event.query.trim().length < 1) {
    return;
  }

  filteredStudents.value = students.value.filter((student) => {
    return student.full_name.toLowerCase().search(event.query.toLowerCase()) >= 0;
  });
};

const canScheduleEvaluations = computed(() => usePersonTypes.isClinician());
</script>

<template>
  <Card class="page-content-full">
    <template #header>
      <div class="align-items-center flex justify-content-between w-full">
        <div class="flex">
          <span class="material-icons mr-2" style="color: #078888">assignment_turned_in</span>
          <h2>Evaluations</h2>
        </div>
        <div class="align-items-center bg-light-gray border-round-sm flex justify-content-center p-2">
          <p class="text-dark text-xs mr-1">Completed</p>
          <div v-if="!loading" class="text-sm text-primary-dark font-bold" v-text="completed" />
          <Skeleton v-else shape="rectangle" size="10px" />
        </div>
      </div>
    </template>
    <template #content>
      <div class="w-full p-input-icon-left">
        <IconField icon-position="left">
          <InputIcon class="p-input-icon-left" style="z-index: 100">
            <Icon name="search" class="text-lg" />
          </InputIcon>
          <AutoComplete
            v-model="selectedStudent"
            force-selection
            class="autocomplete w-full"
            input-class="w-full pl-5"
            :suggestions="filteredStudents"
            placeholder="Search Students"
            :min-length="2"
            field="full_name"
            data-key="id"
            @complete="searchStudent($event)"
          />
        </IconField>
      </div>
      <div class="p-datatable p-component p-datatable-scrollable p-datatable-responsive-scroll mt-3">
        <div class="p-datatable-wrapper">
          <table role="table" class="p-datatable-table text-sm">
            <thead class="p-datatable-thead" role="rowgroup">
              <tr>
                <th>
                  <div class="p-column-header-content">
                    <span class="p-column-title">Student</span>
                  </div>
                </th>
                <th>
                  <div class="p-column-header-content">
                    <span class="p-column-title">Due Date</span>
                  </div>
                </th>
                <th>
                  <div class="p-column-header-content">
                    <span class="p-column-title">Scheduled</span>
                  </div>
                </th>
              </tr>
            </thead>
            <ScrollPanel v-if="!loading" style="width: 100%; height: 400px">
              <tbody class="p-datatable-tbody block" role="rowgroup">
                <tr>
                  <td class="p-1 pl-3 bg-extra-light text-dark-red border-none" colspan="3">Overdue</td>
                </tr>
                <tr v-for="data in overdueStudents" :key="`${data.person.id}${data.due_at}${data.discipline.id}`">
                  <td>
                    <p v-text="data.person.full_name" />
                    <DisciplineIcon classes="mx-2" :code="data.discipline.code" />
                  </td>
                  <td class="font-semibold text-dark-red" v-text="data.due_at_formatted" />
                  <td v-if="data.scheduled_date" v-text="data.scheduled_date" />
                  <td v-else>
                    <div
                      v-if="canScheduleEvaluations"
                      class="flex align-items-center text-primary-dark font-semibold cursor-pointer"
                      @click="openScheduleEvent(data.person.id, data.due_at)"
                    >
                      <Icon name="add" class="text-primary-medium" />
                      Schedule Evaluation
                    </div>
                  </td>
                </tr>
                <tr v-if="overdueStudents.length <= 0">
                  <td colspan="3">No evaluations overdue.</td>
                </tr>
                <tr><td class="p-1 pl-3 bg-extra-light text-dark-medium border-none" colspan="3">Upcoming</td></tr>
                <tr v-for="(data, index) in upcomingStudents" :key="index">
                  <td>
                    <p v-text="data.person.full_name" />
                    <DisciplineIcon classes="mx-2" :code="data.discipline.code" />
                  </td>
                  <td v-text="data.due_at_formatted" />
                  <td v-if="data.scheduled_date" v-text="data.scheduled_date" />
                  <td v-else>
                    <div
                      v-if="canScheduleEvaluations"
                      class="flex align-items-center text-primary-dark font-semibold cursor-pointer"
                      @click="openScheduleEvent(data.person.id, data.due_at)"
                    >
                      <Icon name="add" class="text-primary-medium" />
                      Schedule Evaluation
                    </div>
                  </td>
                </tr>
                <tr v-if="upcomingStudents.length <= 0">
                  <td colspan="3">No evaluations upcoming.</td>
                </tr>
              </tbody>
            </ScrollPanel>
            <div v-for="i in new Array(8)" v-else :key="i">
              <Skeleton class="my-1" height="53px" />
            </div>
          </table>
        </div>
        <div class="p-column-resizer-helper hidden" />
      </div>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  .p-datatable-header {
    background: $white;
    padding: 0.5rem;
  }

  .p-datatable-wrapper {
    margin: 0px !important;
  }

  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }
}

.bg-light-gray {
  background: $grayLight;
}

.text-primary-dark {
  color: var(--primary-color-dark);
}

.text-dark {
  color: #3b4242;
}

.text-red-600 {
  color: $red;
}

.bg-extra-light {
  background: var(--primary-color-extra-light);
}

.text-dark-red {
  color: #822508;
}

.text-dark-medium {
  color: var(--text-color-secondary);
}

.text-primary-medium {
  color: #8bcfcf;
}
</style>
